/**
 * The response envelope. One success shape, one failure shape, no exceptions.
 *
 * Both sides import this module, so the client cannot drift from what the server
 * sends. The old API had no envelope at all: some routes returned a bare document,
 * some `{ ok: true, email }`, some `{ error: message }`, and the client had five
 * separate helpers each guessing.
 *
 * Success:  { ok: true,  data, meta? }
 * Failure:  { ok: false, error: { code, message, details, correlationId } }
 *
 * `meta` carries pagination and nothing else.
 */

import { API_ERROR_CODES, DEFAULT_MESSAGE_BY_ERROR_CODE } from './api-error.js';

/**
 * Wrap a payload as a success response.
 *
 * @param {unknown} data
 * @param {object|null} [meta] Pagination only.
 * @returns {{ok: true, data: unknown, meta?: object}}
 */
export function successEnvelope(data, meta = null) {
  return meta ? { ok: true, data, meta } : { ok: true, data };
}

/**
 * Wrap a failure as an error response.
 *
 * @param {object} params
 * @param {string} params.code
 * @param {string} [params.message]
 * @param {object|null} [params.details]
 * @param {string|null} [params.correlationId]
 * @returns {{ok: false, error: object}}
 */
export function errorEnvelope({ code, message, details = null, correlationId = null }) {
  return {
    ok: false,
    error: {
      code,
      message: message ?? DEFAULT_MESSAGE_BY_ERROR_CODE[code] ?? 'Request failed',
      details,
      correlationId,
    },
  };
}

/**
 * Whether a parsed body is a well-formed success envelope.
 *
 * @param {unknown} body
 * @returns {boolean}
 */
export function isSuccessEnvelope(body) {
  return Boolean(body) && body.ok === true && 'data' in body;
}

/**
 * Whether a parsed body is a well-formed error envelope.
 *
 * @param {unknown} body
 * @returns {boolean}
 */
export function isErrorEnvelope(body) {
  return Boolean(body) && body.ok === false && typeof body.error?.code === 'string';
}

/**
 * Build the `meta` block for a paginated list.
 *
 * Pagination is cursor based, not offset based: a health record grows and an offset
 * page shifts under the reader as rows are added. The old API had no pagination on
 * any list, including the activity log and the calendar, both of which grow without
 * bound.
 *
 * @param {object} params
 * @param {string|null} params.nextCursor  Opaque. Null when the list is exhausted.
 * @param {number} params.limit            Page size actually applied.
 * @returns {{nextCursor: string|null, limit: number, hasMore: boolean}}
 */
export function paginationMeta({ nextCursor, limit }) {
  return { nextCursor: nextCursor ?? null, limit, hasMore: Boolean(nextCursor) };
}

/** Default page size when a caller does not ask for one. */
export const DEFAULT_PAGE_SIZE = 50;

/** Largest page a caller may request, so one request cannot pull a whole history. */
export const MAX_PAGE_SIZE = 200;

/**
 * Body size limits, from a small named set.
 *
 * The old server had nine ad hoc values between 4kb and 50mb chosen per route, and
 * the 50mb one sat on an unauthenticated image upload.
 */
export const BODY_LIMITS = Object.freeze({
  /** Credentials, toggles, single field patches. */
  small: '16kb',
  /** Normal feature payloads: a day's edits, a list item, a profile. */
  standard: '128kb',
  /** Backup import, which is the only legitimately large JSON body. */
  large: '4mb',
});

/** Every code, re-exported so a consumer needs one import for the envelope. */
export { API_ERROR_CODES };
